import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { AddressComponent } from '../address/address.component';
import { IApplicantInformation } from 'app/models/licenses/applicant-information';
import { ApplicantInformationService } from 'app/services/license/applicant-information.service';
import { AuthService } from '@shared/auth/auth.service';

@Component({
  selector: 'applicant-information',
  templateUrl: './applicant-information.component.html',
  styleUrls: ['./applicant-information.component.scss']
})

export class ApplicantInformationComponent implements OnInit {
  @ViewChild(AddressComponent) addresses: AddressComponent;
  @Input() licenseMasterId: number = 0;
  @Input() isChange: boolean = false;
  @Input() isEditable: boolean = false;
  public isOutOfState: boolean = false;
  public includeArchived: boolean = false;
  public applicationEntityType: string = 'ApplicantInformation';
  public applicationId: number = 0;
  public fullSSN: string = ''; // Stores the full SSN
  public maskedSSN: string = ''; // Displays the masked version
  public ssnInvalid: boolean = false;
  public ssnVisible: boolean = false; // Toggle input visibility when editing
  public showSsn: boolean = false;
  public applicantInformation: IApplicantInformation = {
    id: 0,
    licenseMasterId: 0,
    firstName: '',
    lastName: '',
    middleName: '',
    phone: '',
    email: '',
    ssn: '',
    dateOfBirth: '',
    priorName: '',
    gender: ''
  }
  public sharedService = inject(SharedService);
  public applicantInfoService = inject(ApplicantInformationService);
  public authService = inject(AuthService);

  public applicantInfoForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    middleName: new FormControl(''),
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    ssn: new FormControl('', [Validators.required, Validators.minLength(9), Validators.maxLength(9)]),
    dateOfBirth: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    gender: new FormControl('', Validators.required),
    priorName: new FormControl('')
  });

  ngOnInit(): void {
    if (this.licenseMasterId > 0) {
      this.getApplicantInformation();
    }
  }

  getApplicantInformation(): void {
    this.applicantInfoService.getApplicantInformation(this.licenseMasterId).subscribe({
      next: response => {
        if (response) {
          this.applicantInformation = response;
          this.applicationId = this.applicantInformation.id;
          this.updateApplicantInformationForm(this.applicantInformation);
        } else {
          if (this.isEditable) {
            // If no application info exists, create a new one
            this.createApplicationInformation();
          }
        }
      },
      error: e => {
        console.error('Error fetching applicant information:', e);
        this.sharedService.toastr.error('Error fetching applicant information');
      }
    });
  }

  updateApplicantInformationForm(applicantInfo: IApplicantInformation): void {
    this.applicantInfoForm.reset({
      firstName: applicantInfo.firstName,
      lastName: applicantInfo.lastName,
      phone: applicantInfo.phone,
      email: applicantInfo.email,
      ssn: applicantInfo.ssn,
      dateOfBirth: applicantInfo.dateOfBirth,
      middleName: applicantInfo.middleName,
      gender: applicantInfo.gender,
      priorName: applicantInfo.priorName
    });
  }

  createApplicationInformation(): void {
    this.applicantInformation.licenseMasterId = this.licenseMasterId;
    this.applicantInformation.email = this.email;
    this.saveApplicantInfo();
  }

  updateApplicationInformation(): void {
    let form = this.applicantInfoForm.value;
    this.applicantInformation.firstName = form.firstName;
    this.applicantInformation.lastName = form.lastName;
    this.applicantInformation.phone = form.phone;
    this.applicantInformation.middleName = form.middleName;
    this.applicantInformation.ssn = form.ssn;
    this.applicantInformation.dateOfBirth = form.dateOfBirth && form.dateOfBirth !== '' ? form.dateOfBirth : null;
    this.applicantInformation.email = form.email;
    this.applicantInformation.gender = form.gender;
    this.applicantInformation.priorName = form.priorName;
    this.applicantInformation.licenseMasterId = this.licenseMasterId;
  }

  saveApplicantInfo(): boolean {
    this.updateApplicationInformation();
    this.applicantInfoService.saveApplicantInformation(this.applicantInformation).subscribe({
      next: (response) => {
        this.applicationId = response;
        this.applicantInformation.id = response;
      },
      error: (error) => {
        console.error('Error saving applicant information:', error);
        return false;
      },
      complete: () => {
        return true;
      }
    });
    return false;
  }

  toggleSsn(): void {
    this.showSsn = !this.showSsn;
  }

  public get email(): string {
    if (!this.authService || this.authService.email == null) {
      return "";
    }
    return this.authService.email;
  }

  validateApplicantInformation(): string[] {
    let errors: string[] = [];
    if (this.applicantInfoForm.invalid) {
      errors.push('Applicant details are incomplete');
    }
    return errors;
  }

  validateAddresses(): string[] {
    let errors: string[] = [];

    if (this.addresses?.addresses?.length === 0) {
      errors.push('Please add at least one address');
    }

    return errors;
  }
}
